import React, { useState, useEffect } from 'react';
import './Sidebar.css';
import { getTournamentType } from '../utils/dataProcessor';

const Sidebar = ({ data, filters, onFilterChange }) => {
  const [availableOptions, setAvailableOptions] = useState({
    tournaments: [],
    dates: [],
    courts: [],
    graphics: [],
    tournamentTypes: [], 
    sponsors: []
  });

  useEffect(() => {
    if (data && data.length > 0) {
      try {
        // Always get all tournaments for the tournament filter
        const tournaments = [];
        data.forEach(row => {
          if (row.Tournament && !tournaments.includes(row.Tournament)) {
            tournaments.push(row.Tournament);
          }
        });

        // Custom sorting function for tournaments that handles 's-Hertogenbosch
        const sortTournaments = (a, b) => {
          // Remove apostrophes and convert to lowercase for comparison
          const aClean = a.replace(/'/g, '').toLowerCase();
          const bClean = b.replace(/'/g, '').toLowerCase();
          
          // Sort alphabetically
          if (aClean < bClean) return -1;
          if (aClean > bClean) return 1;
          return 0;
        };

        // Extract tournament types
        const tournamentTypes = [];
        const tournamentTypeDebug = new Map(); // Debug: track which tournaments map to which types
        data.forEach(row => {
          if (row.Tournament) {
            const type = getTournamentType(row);
            if (type) {
              if (!tournamentTypeDebug.has(row.Tournament)) {
                tournamentTypeDebug.set(row.Tournament, { type, eventId: row.EventId });
              }
              if (!tournamentTypes.includes(type)) {
                tournamentTypes.push(type);
              }
            }
          }
        });
        
        console.log('🎾 Tournament Type Mapping:', Array.from(tournamentTypeDebug.entries()).map(([name, info]) => `${name} (ID: ${info.eventId}) -> ${info.type}`));
        console.log('🎾 Available Tournament Types:', tournamentTypes);
        
        // Sort tournament types in a specific order
        const typeOrder = ['1000', '500', '250', 'WTF', 'NextGen', 'Other'];
        tournamentTypes.sort((a, b) => {
          const indexA = typeOrder.indexOf(a);
          const indexB = typeOrder.indexOf(b);
          return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
        });

        // Filter data based on selected tournaments and tournament types for other options
        let filteredData = data;
        if (filters.tournaments && filters.tournaments.length > 0) {
          filteredData = filteredData.filter(row => filters.tournaments.includes(row.Tournament));
        }
        if (filters.tournamentTypes && filters.tournamentTypes.length > 0) {
          filteredData = filteredData.filter(row => {
            const type = getTournamentType(row);
            return filters.tournamentTypes.includes(type);
          });
        }

        // Extract unique values from filtered data
        const dates = [];
          const courts = [];
          const graphics = [];
          const sponsors = [];

        filteredData.forEach(row => {
          if (row.Date && !dates.includes(row.Date)) {
            dates.push(row.Date);
          }
          if (row['Court Name'] && !courts.includes(row['Court Name'])) {
            courts.push(row['Court Name']);
          }
          if (row['Graphic Type'] && !graphics.includes(row['Graphic Type'])) {
            graphics.push(row['Graphic Type']);
            }
            if (row.Sponsor && !sponsors.includes(row.Sponsor)) {
                sponsors.push(row.Sponsor);
            }
        });

        setAvailableOptions({
          tournaments: tournaments.sort(sortTournaments),
          dates: dates.sort(),
          courts: courts.sort(),
          graphics: graphics.sort(),
            tournamentTypes: tournamentTypes,
            sponsors: sponsors.sort()
        });
      } catch (error) {
        console.error('Error processing data for filters:', error);
      }
    }
  }, [data, filters.tournaments, filters.tournamentTypes]);

  const handleMultiSelect = (filterType, selectedOptions) => {
    try {
      onFilterChange(filterType, selectedOptions);
      
      // If tournaments or tournamentTypes change, clear dependent filters since their options will change
      if (filterType === 'tournaments' || filterType === 'tournamentTypes') {
        onFilterChange('dates', []);
        onFilterChange('courts', []);
          onFilterChange('graphics', []);
          onFilterChange('sponsors', []);
      }
    } catch (error) {
      console.error('Error changing filter:', error);
    }
  };

  const clearAllFilters = () => {
    try {
      onFilterChange('tournaments', []);
      onFilterChange('tournamentTypes', []);
      onFilterChange('dates', []);
      onFilterChange('courts', []);
        onFilterChange('graphics', []);
        onFilterChange('sponsors', []);
    } catch (error) {
      console.error('Error clearing filters:', error);
    }
  };

  if (!data) return null;

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 4C3 3.44772 3.44772 3 4 3H20C20.5523 3 21 3.44772 21 4V6.58579C21 6.851 20.8946 7.10536 20.7071 7.29289L14.2929 13.7071C14.1054 13.8946 14 14.149 14 14.4142V17L10 21V14.4142C10 14.149 9.89464 13.8946 9.70711 13.7071L3.29289 7.29289C3.10536 7.10536 3 6.851 3 6.58579V4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Filters
        </h3>
        <label>Ctrl + Click to Select Multiple</label>
        <button 
          onClick={clearAllFilters}
          className="clear-filters-btn"
          title="Clear all filters"
        >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px' }}>
              <path d="M3 6H5H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Clear All
        </button>
      </div>

      <div className="filter-section">
        <label>Select Tournaments</label>
        <select
          multiple
          value={filters.tournaments || []}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions, option => option.value);
            handleMultiSelect('tournaments', selected);
          }}
          className="filter-select"
        >
          {availableOptions.tournaments.map(tournament => (
            <option key={tournament} value={tournament}>
              {tournament}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-section">
        <label>Tournament Type</label>
        <select
          multiple
          value={filters.tournamentTypes || []}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions, option => option.value);
            handleMultiSelect('tournamentTypes', selected);
          }}
          className="filter-select"
        >
          {availableOptions.tournamentTypes.map(type => (
            <option key={type} value={type}>
              {type === '1000' ? 'ATP 1000 (Masters)' : 
               type === '500' ? 'ATP 500' : 
               type === '250' ? 'ATP 250' : 
               type === 'WTF' ? 'ATP Finals' :
               type === 'NextGen' ? 'NextGen Finals' :
               type}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-section">
        <label>Select Dates</label>
        <select
          multiple
          value={filters.dates || []}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions, option => option.value);
            handleMultiSelect('dates', selected);
          }}
          className="filter-select"
        >
          {availableOptions.dates.map(date => (
            <option key={date} value={date}>
              {new Date(date).toLocaleDateString()}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-section">
        <label>Select Courts</label>
        <select
          multiple
          value={filters.courts || []}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions, option => option.value);
            handleMultiSelect('courts', selected);
          }}
          className="filter-select"
        >
          {availableOptions.courts.map(court => (
            <option key={court} value={court}>
              {court}
            </option>
          ))}
        </select>
          </div>

        <div className="filter-section">
            <label>Select Sponsors</label>

            <select
                multiple
                value={filters.sponsors || []}
                onChange={(e) => {
                    const selected = Array.from(
                        e.target.selectedOptions,
                        option => option.value
                    );

                    handleMultiSelect('sponsors', selected);
                }}
                className="filter-select"
            >
                {availableOptions.sponsors.map(sponsor => (
                    <option key={sponsor} value={sponsor}>
                        {sponsor}
                    </option>
                ))}
            </select>
        </div>

      <div className="filter-section">
        <label>Select Graphics</label>
        <select
          multiple
          value={filters.graphics || []}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions, option => option.value);
            handleMultiSelect('graphics', selected);
          }}
          className="filter-select"
        >
          {availableOptions.graphics.map(graphic => (
            <option key={graphic} value={graphic}>
              {graphic}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default Sidebar; 